import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function QCPage() {
  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <div className="container-main pt-24 pb-16">
        <div className="max-w-3xl mx-auto">
          <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-6">
            <a href="/" className="hover:text-primary-600 transition-colors">Home</a>
            <i className="ri-arrow-right-s-line text-xs" />
            <span className="text-foreground-600">QC Guide</span>
          </nav>

          <span className="inline-block px-2.5 py-1 rounded text-xs font-medium bg-secondary-100 text-secondary-700 mb-3">QC</span>
          <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4">
            AllChinaBuy QC Photos Guide: What to Look For
          </h1>
          <div className="flex items-center gap-4 text-sm text-foreground-400 mb-8">
            <span className="flex items-center gap-1"><i className="ri-calendar-line" /> Updated August 25, 2026</span>
            <span className="flex items-center gap-1"><i className="ri-time-line" /> 7 min read</span>
          </div>

          <div className="rounded-lg overflow-hidden mb-8">
            <img
              src="https://readdy.ai/api/search-image?query=Quality%20control%20inspection%20concept%20with%20magnifying%20glass%20examining%20product%20details%20on%20clean%20white%20surface%2C%20quality%20assurance%20theme%2C%20warm%20beige%20and%20cream%20tones%2C%20editorial%20photography%20style%2C%20soft%20natural%20lighting%2C%20professional%20aesthetic%20with%20subtle%20warm%20glow&width=1200&height=600&seq=article-qc-hero-001&orientation=landscape"
              alt="AllChinaBuy QC Guide"
              className="w-full h-64 md:h-80 object-cover"
            />
          </div>

          <div className="prose prose-sm max-w-none text-foreground-700 leading-relaxed">
            <p className="text-base mb-6">
              <strong>Quality Control (QC) photos</strong> are one of the most valuable features of the AllChinaBuy buying process. These photos show the actual items that arrived at the warehouse before they are shipped to you. Learning how to read QC photos effectively can save you from receiving disappointing products.
            </p>

            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4">What Are QC Photos?</h2>
            <p className="text-base mb-4">
              When your seller ships items to the AllChinaBuy warehouse, staff photograph the products from multiple angles. These QC photos are uploaded to your order page, allowing you to inspect the actual item before approving international shipment. This is your last chance to catch defects, wrong items, or quality issues before paying for costly international shipping.
            </p>

            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4">QC Checklist by Category</h2>

            <div className="bg-background-100 rounded-lg p-5 border border-background-200/50 mb-6">
              <h3 className="font-medium text-sm text-foreground-900 mb-3">Shoes</h3>
              <ul className="space-y-1.5 text-sm text-foreground-700">
                <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line text-accent-500 mt-0.5" /> Check logo placement and quality</li>
                <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line text-accent-500 mt-0.5" /> Verify stitching is even and clean</li>
                <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line text-accent-500 mt-0.5" /> Check for glue stains or excess adhesive</li>
                <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line text-accent-500 mt-0.5" /> Confirm correct colorway matches listing</li>
                <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line text-accent-500 mt-0.5" /> Inspect sole texture and pattern</li>
              </ul>
            </div>

            <div className="bg-background-100 rounded-lg p-5 border border-background-200/50 mb-6">
              <h3 className="font-medium text-sm text-foreground-900 mb-3">Clothing</h3>
              <ul className="space-y-1.5 text-sm text-foreground-700">
                <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line text-accent-500 mt-0.5" /> Check print quality and alignment</li>
                <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line text-accent-500 mt-0.5" /> Verify fabric matches description</li>
                <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line text-accent-500 mt-0.5" /> Inspect tags and labels for accuracy</li>
                <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line text-accent-500 mt-0.5" /> Check for loose threads or defects</li>
                <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line text-accent-500 mt-0.5" /> Confirm size matches your order</li>
              </ul>
            </div>

            <div className="bg-background-100 rounded-lg p-5 border border-background-200/50 mb-6">
              <h3 className="font-medium text-sm text-foreground-900 mb-3">Bags & Accessories</h3>
              <ul className="space-y-1.5 text-sm text-foreground-700">
                <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line text-accent-500 mt-0.5" /> Check hardware quality (zippers, clasps, buckles)</li>
                <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line text-accent-500 mt-0.5" /> Verify logo engraving and placement</li>
                <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line text-accent-500 mt-0.5" /> Inspect stitching around stress points</li>
                <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line text-accent-500 mt-0.5" /> Check interior lining and pockets</li>
                <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line text-accent-500 mt-0.5" /> Confirm shape and structure are correct</li>
              </ul>
            </div>

            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4">When to Approve vs. Reject</h2>
            <p className="text-base mb-4">Use this framework to decide whether to approve or reject an item:</p>

            <div className="overflow-x-auto mb-6">
              <table className="w-full text-sm border border-background-200/70 rounded-lg">
                <thead>
                  <tr className="bg-background-100">
                    <th className="px-4 py-3 text-left font-medium text-foreground-700">Issue</th>
                    <th className="px-4 py-3 text-left font-medium text-foreground-700">Recommendation</th>
                    <th className="px-4 py-3 text-left font-medium text-foreground-700">Reason</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-200/50">
                  <tr>
                    <td className="px-4 py-3 font-medium">Wrong item sent</td>
                    <td className="px-4 py-3"><span className="text-primary-700 font-medium">Reject</span></td>
                    <td className="px-4 py-3 text-foreground-600">Never accept incorrect items</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Major defect visible</td>
                    <td className="px-4 py-3"><span className="text-primary-700 font-medium">Reject</span></td>
                    <td className="px-4 py-3 text-foreground-600">Stains, tears, broken hardware</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Minor stitching flaw</td>
                    <td className="px-4 py-3"><span className="text-secondary-700 font-medium">Consider</span></td>
                    <td className="px-4 py-3 text-foreground-600">Small issues may be acceptable for the price</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Slightly off color</td>
                    <td className="px-4 py-3"><span className="text-secondary-700 font-medium">Consider</span></td>
                    <td className="px-4 py-3 text-foreground-600">Lighting in photos may affect appearance</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Item matches listing</td>
                    <td className="px-4 py-3"><span className="text-accent-700 font-medium">Approve</span></td>
                    <td className="px-4 py-3 text-foreground-600">Quality is acceptable for the price point</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4">Tips for Better QC Photos</h2>
            <ul className="space-y-2 mb-6">
              <li className="flex items-start gap-2"><i className="ri-lightbulb-line text-primary-500 mt-0.5" /> Request additional photos if the standard set is unclear</li>
              <li className="flex items-start gap-2"><i className="ri-lightbulb-line text-primary-500 mt-0.5" /> Ask for specific angles if you have particular concerns</li>
              <li className="flex items-start gap-2"><i className="ri-lightbulb-line text-primary-500 mt-0.5" /> Compare photos with original listing images side by side</li>
              <li className="flex items-start gap-2"><i className="ri-lightbulb-line text-primary-500 mt-0.5" /> Join community forums to see what others received from the same seller</li>
            </ul>

            <p className="text-base mb-6">
              For more buying guides, read our <a href="/allchinabuy-spreadsheet-complete-guide" className="text-primary-600 hover:underline">Complete Guide</a> or <a href="/is-allchinabuy-legit" className="text-primary-600 hover:underline">Safety Guide</a>.
            </p>

            <div className="mt-10 bg-primary-50 rounded-lg p-6 md:p-8 text-center border border-primary-200/50">
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">Ready to Shop Smarter?</h3>
              <p className="text-sm text-foreground-600 mb-4">Browse our spreadsheet resources to find quality sellers with good QC track records.</p>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="btn-primary inline-flex">
                Visit AllChinaBuy Spreadsheet
                <i className="ri-external-link-line ml-1" />
              </a>
            </div>

            <div className="mt-10 pt-6 border-t border-background-200/50">
              <p className="text-xs font-semibold uppercase tracking-wider text-foreground-400 mb-3">Related Articles</p>
              <div className="flex flex-wrap gap-2">
                <a href="/allchinabuy-spreadsheet-complete-guide" className="px-3 py-1.5 rounded-md bg-background-100 border border-background-200/50 text-sm text-foreground-600 hover:border-primary-300 hover:text-primary-700 transition-colors">Complete Guide</a>
                <a href="/is-allchinabuy-legit" className="px-3 py-1.5 rounded-md bg-background-100 border border-background-200/50 text-sm text-foreground-600 hover:border-primary-300 hover:text-primary-700 transition-colors">Safety Guide</a>
                <a href="/allchinabuy-shipping-cost-guide" className="px-3 py-1.5 rounded-md bg-background-100 border border-background-200/50 text-sm text-foreground-600 hover:border-primary-300 hover:text-primary-700 transition-colors">Shipping Guide</a>
                <a href="/allchinabuy-review" className="px-3 py-1.5 rounded-md bg-background-100 border border-background-200/50 text-sm text-foreground-600 hover:border-primary-300 hover:text-primary-700 transition-colors">Platform Review</a>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}